import type { MessageInstance } from 'antd/es/message/interface'
import type { NotificationInstance } from 'antd/es/notification/interface'

/**
 * Holder for antd notification / message instances extracted from `App.useApp()`.
 *
 * This allows non-React code (RTK base queries, Redux middleware, plain
 * utilities) to trigger notifications without importing the static API —
 * which cannot consume dynamic theme or ConfigProvider context.
 *
 * Usage:
 *   1. In the App wrapper, call `useStaticNotification()` to bind the instances.
 *   2. Anywhere else, import `staticNotification` / `staticMessage` and call
 *      them exactly like the hook-based instances.
 *
 * @see https://ant.design/components/app#how-to-use-static-methods
 */

let notificationInstance: NotificationInstance | null = null
let messageInstance: MessageInstance | null = null

export function setStaticNotification(instance: NotificationInstance) {
  notificationInstance = instance
}

export function setStaticMessage(instance: MessageInstance) {
  messageInstance = instance
}

export function getStaticNotification(): NotificationInstance {
  if (!notificationInstance) {
    throw new Error(
      '[static-notification] Notification instance not set. ' +
        'Ensure useStaticNotification() is called inside the <App> wrapper.'
    )
  }
  return notificationInstance
}

export function getStaticMessage(): MessageInstance {
  if (!messageInstance) {
    throw new Error(
      '[static-notification] Message instance not set. ' +
        'Ensure useStaticNotification() is called inside the <App> wrapper.'
    )
  }
  return messageInstance
}

/**
 * Proxy object — drop-in replacement for `notification` from antd.
 * Calls are forwarded to the App-scoped instance at invocation time.
 */
export const staticNotification: NotificationInstance = new Proxy(
  {} as NotificationInstance,
  {
    get(_target, prop: keyof NotificationInstance) {
      return (...args: unknown[]) => {
        const instance = getStaticNotification()
        return (instance[prop] as (...a: unknown[]) => unknown)(...args)
      }
    },
  }
)

export const staticMessage: MessageInstance = new Proxy(
  {} as MessageInstance,
  {
    get(_target, prop: keyof MessageInstance) {
      return (...args: unknown[]) => {
        const instance = getStaticMessage()
        return (instance[prop] as (...a: unknown[]) => unknown)(...args)
      }
    },
  }
)
